import React, { useMemo, useState } from 'react';
import RowGroup from '../RowGroup';
import { SCRUBBER_API_BASE } from '../../api/config';

/**
 * FDRUniverseReview
 *
 * Post-upload review screen shown to the FDR inside their portal session.
 * Renders the same annotated table the plan-side viewer renders, but stripped
 * of plan-only controls (Submit-to-Compliance, ML Classification, export).
 *
 * Props:
 *   submission        — { id, attempt_number, attempts_remaining, file_name, file_size, validation }
 *   universe          — full annotated row array (same shape as /api/upload returns)
 *   errors            — flat error array
 *   categoryCounts    — { [category]: count }
 *   invitationToken   — magic-link token (for the Authorization Bearer header)
 *   mfaPass           — HMAC-signed pass token (for the X-MFA-Pass header)
 *   onRequestResubmit — callback: parent takes user back to the file-picker step
 *   onFinalized       — callback: parent shows the "submission complete" screen
 *
 * Plain-language category labels are intentional: an FDR data clerk may not
 * know the SOC 2 / CMS lexicon the validator uses internally.
 */

// Plain-language tooltips. The KEYS match server-emitted categories exactly.
const CATEGORY_BLURBS = {
  'Data Accuracy':     'A value looks incorrect, out of range, or fails a content check.',
  'Timeliness':        'A case took longer than CMS allows (per protocol § for this universe).',
  'Missing Data':      'A required field is blank.',
  'Formatting Issues': 'A value does not match the expected format (e.g., wrong date pattern).',
  'Missing Info':      'A required column is missing from the file.',
  'Bad Format':        'An unexpected column was found in the file.',
  'Wrong Order':       'A column is present but in the wrong position.',
  'Universe Period':   'The case date falls outside the universe period your plan requested.',
  'Validation Error':  'We could not validate this row — please check the file format.',
};

function CategoryBlurb({ category }) {
  const text = CATEGORY_BLURBS[category];
  if (!text) return null;
  return (
    <span className="ml-1 text-[10px] text-slate-500 font-normal hidden sm:inline">
      — {text}
    </span>
  );
}

function classifyRow(row) {
  const cats = new Set();
  for (const cell of Object.values(row.data || {})) {
    if (cell.category) cats.add(cell.category);
  }
  if (row.timelinessCategory) cats.add(row.timelinessCategory);
  return cats;
}

export default function FDRUniverseReview({
  submission,
  universe,
  errors,
  categoryCounts,
  invitationToken,
  mfaPass,
  onRequestResubmit,
  onFinalized,
}) {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [onlyShowErroredRows, setOnlyShowErroredRows] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState(null);

  const allFields = useMemo(
    () => (universe && universe.length > 0 ? Object.keys(universe[0].data || {}) : []),
    [universe]
  );

  const filteredRows = useMemo(() => {
    if (!universe) return [];
    return universe.filter((row) => {
      const rowCats = classifyRow(row);
      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.some((c) => rowCats.has(c));
      const hasIssue = rowCats.size > 0;
      return matchesCategory && (!onlyShowErroredRows || hasIssue);
    });
  }, [universe, selectedCategories, onlyShowErroredRows]);

  const categories = Object.keys(categoryCounts || {});
  const totalIssues = Object.values(categoryCounts || {}).reduce((a, b) => a + b, 0);
  const hasIssues = totalIssues > 0;

  const toggleCategory = (cat) =>
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );

  const submitFinal = async () => {
    const issueWarning = hasIssues
      ? `Your submission still has ${totalIssues} validation issue${totalIssues === 1 ? '' : 's'}. ` +
        `The plan will see these issues alongside your submission.\n\n` +
        `Submit as final anyway?`
      : `Your submission is clean — no validation issues found.\n\nSubmit as final to the plan?`;
    if (!window.confirm(issueWarning)) return;

    setFinalizing(true);
    setError(null);
    try {
      const resp = await fetch(`${SCRUBBER_API_BASE}/api/fdr/portal/finalize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${invitationToken}`,
          'X-MFA-Pass': mfaPass,
        },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        throw new Error(data.message || `Finalize failed (HTTP ${resp.status})`);
      }
      onFinalized(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setFinalizing(false);
    }
  };

  const requestResubmit = () => {
    if (submission.attempts_remaining <= 0) {
      setError('You have no more attempts. The current submission will be sent as final if you continue.');
      return;
    }
    const confirmText =
      `Replace this submission with a corrected file? ` +
      `You have ${submission.attempts_remaining} attempt${submission.attempts_remaining === 1 ? '' : 's'} remaining after this one.`;
    if (!window.confirm(confirmText)) return;
    onRequestResubmit();
  };

  if (!universe || universe.length === 0) {
    return (
      <div className="p-6 text-center text-slate-500">
        We received your file but could not read any rows. Please check that your
        file matches the expected format and try uploading again.
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-slate-900">Review your submission</h2>
        <p className="text-sm text-slate-500 mt-1">
          These are the results of scanning your file against the CMS protocol for{' '}
          <span className="font-semibold text-slate-700">this universe</span>. Review the
          flagged rows below. When you're ready, you can submit as-is, or upload a
          corrected file.
        </p>
      </div>

      {/* Attempt + summary banner */}
      <div className={`rounded-xl border p-4 mb-5 ${hasIssues ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className={`text-sm font-semibold ${hasIssues ? 'text-amber-800' : 'text-emerald-800'}`}>
              {hasIssues
                ? `Found ${totalIssues} issue${totalIssues === 1 ? '' : 's'} across ${submission.validation.row_errors || 0} row${submission.validation.row_errors === 1 ? '' : 's'}.`
                : `No validation issues found. ${submission.validation.total_rows} rows checked.`}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              File: <span className="font-mono">{submission.file_name}</span> · {(submission.file_size / 1024).toFixed(1)} KB ·{' '}
              attempt {submission.attempt_number} of {submission.attempt_number + submission.attempts_remaining}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Attempts left</div>
            <div className={`text-2xl font-bold ${submission.attempts_remaining > 0 ? 'text-slate-800' : 'text-rose-600'}`}>
              {submission.attempts_remaining}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {categories.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold mr-1">Filter:</span>
          {categories.map((cat) => {
            const isSelected = selectedCategories.includes(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => toggleCategory(cat)}
                title={CATEGORY_BLURBS[cat] || ''}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  isSelected
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                }`}
              >
                <span>{cat}</span>
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[10px] ${isSelected ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700'}`}>
                  {categoryCounts[cat]}
                </span>
                <CategoryBlurb category={cat} />
              </button>
            );
          })}
          <label className="ml-auto inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyShowErroredRows}
              onChange={(e) => setOnlyShowErroredRows(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Only show rows with issues
          </label>
        </div>
      )}

      {/* Row counter */}
      <div className="text-xs text-slate-500 mb-2">
        Showing <span className="font-semibold text-slate-700">{filteredRows.length}</span> of {universe.length} rows
      </div>

      {/* Annotated table — same RowGroup component plan staff sees */}
      <div className="rounded-xl border border-slate-200 overflow-auto max-h-[60vh] bg-white">
        <table className="border-collapse text-sm w-full">
          <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-slate-50 z-20 border-r border-slate-200">Row</th>
              {allFields.map((f) => (
                <th key={f} className="px-3 py-2 text-left font-semibold whitespace-nowrap border-r border-slate-200">{f}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <RowGroup
              rows={filteredRows}
              allFields={allFields}
              selectedCategories={selectedCategories}
              onlyShowErroredRows={onlyShowErroredRows}
            />
          </tbody>
        </table>
      </div>

      {/* Action bar */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
        {submission.attempts_remaining > 0 && (
          <button
            type="button"
            onClick={requestResubmit}
            disabled={finalizing}
            className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 font-semibold text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            Upload a corrected file ({submission.attempts_remaining} left)
          </button>
        )}
        <button
          type="button"
          onClick={submitFinal}
          disabled={finalizing}
          className="px-5 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold text-sm"
        >
          {finalizing ? 'Submitting…' : 'Submit final to plan'}
        </button>
      </div>

      {error && (
        <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
          {error}
        </div>
      )}

      <p className="mt-6 text-[11px] text-slate-400 leading-relaxed">
        Submitting as final marks this universe complete for the inviting plan. After
        that, you will not be able to upload again with this link. Every step (upload,
        resubmit, finalize) is recorded in an append-only audit log shared with the plan.
      </p>
    </div>
  );
}
